'use client';

import { BRAND, withBase } from '../../../lib/brand';
import { isRedundantTeaser } from '../../../lib/teaser';
import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { FaMapMarkerAlt, FaStar, FaPhone, FaExternalLinkAlt, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import type { Entity, Feature, NearbyCampsite, PopularRoute, ShopProduct, WeatherForecast, Address } from '../../../types';
import type { SimilarCampsite } from '../../load-entities';
import { entityLocationLabel } from '../../../utils/entityLocation';
import { looksLikeHtml, sanitizeDescriptionHtml, stripHtml } from '../../../lib/richText';
import type { FireDangerDay } from '../../../components/FireDangerWidget';

// ── Existing child components (reused from default template) ──
import ImageGalleryHero from '../../../components/ImageGalleryHero';
import ReviewTeaser from '../../../components/ReviewTeaser';
import StickyNav from '../../../components/StickyNav';
import ShowMoreText from '../../../components/ShowMoreText';
import FeatureGrid from '../../../components/FeatureGrid';
import WeatherForecastComponent from '../../../components/WeatherForecast';
import FishingSpecies from '../../../components/FishingSpecies';
import NearbyActivities from '../../../components/NearbyActivities';
import EntityReviews from '../../../components/EntityReviews';
import PopularRoutesComponent from '../../../components/PopularRoutes';
import VanHireStrip from '../../../components/VanHireStrip';
import NearbyCampsitesComponent from '../../../components/NearbyCampsites';
import CampsiteActions from '../../../components/CampsiteActions';
import FireDangerWidget from '../../../components/FireDangerWidget';
import NearbyFuelStations from '../../../components/NearbyFuelStations';
import SidebarExplorer from '../../../components/SidebarExplorer';
import ShopGearCTA from '../../../components/ShopGearCTA';
import SidebarReviewCTAs from '../../../components/SidebarReviewCTAs';
import ClaimSiteButton from '../../../components/ClaimSiteButton';
import AddressCard from '../addresscard';
import FeatureIcon from '../../../components/FeatureIcon';
import MobileCTABar from '../../../components/MobileCTABar';

// ── Types ────────────────────────────────────────────────────
interface PremiumTemplateProps {
  campsite: Entity;
  address: any;
  allImages: string[];
  heroContributors?: (string | undefined)[];
  /** The app-download promo tile, when the page appended one to allImages. */
  promoImageSrc?: string;
  defaultImage: string;
  weatherForecast: WeatherForecast | null;
  fireDanger: FireDangerDay[];
  nearbyCampsites: NearbyCampsite[];
  similarCampsites: SimilarCampsite[];
  nearbyActivities: any[];
  popularRoutes: PopularRoute[];
  shopProducts: ShopProduct[];
  regions: any[];
  isAccom: boolean;
  isCampingType: boolean;
}

// ── Helpers ──────────────────────────────────────────────────
function getTypeLabel(type?: string) {
  switch (type) {
    case 'free_camping': return 'Free Camping';
    case 'caravan_park': return 'Caravan Park';
    case 'campground': return 'Campground';
    case 'accom': return 'Accommodation';
    case 'tours': return 'Tour / Activity';
    case 'day_rest_area': return 'Rest Area';
    case 'poi': return 'Point of Interest';
    default: return 'Campsite';
  }
}

function getAboutLabel(type?: string) {
  switch (type) {
    case 'accom': return 'About this accommodation';
    case 'tours': return 'About this activity';
    case 'day_rest_area': return 'About this rest area';
    case 'poi': return 'About this location';
    case 'caravan_park': return 'About this caravan park';
    default: return 'About this campsite';
  }
}

const CHIP_FEATURES = new Set([
  'pets welcome', 'power', 'toilets', 'showers', 'shower', 'dump point', 'fishing spot',
  'river fishing', 'beach fishing', 'swimming', 'bbq', 'camp kitchen',
  'drinking water', 'accessible', 'free wifi', 'cabins', 'tent site',
  'caravans', 'motorhomes', 'boat ramp', 'fire place', 'campfire', 'no pets', 'laundromat',
  // US (EscapeTo / OSM feed) canonical term (Free Camping is already shown
  // via the entity-type badge, so it is intentionally not a chip)
  'backcountry',
]);

/* ─── Reusable card wrapper ─────────────────────────────────── */
function SectionCard({ children, className = '', id }: { children: React.ReactNode; className?: string; id?: string }) {
  return (
    <section
      id={id}
      className={`bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-6 sm:p-7 lg:p-8 ${className}`}
    >
      {children}
    </section>
  );
}

function SectionHeading({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <h2 className={`text-[22px] sm:text-2xl font-semibold text-slate-900 tracking-tight ${className}`}>
      {children}
    </h2>
  );
}

function SidebarCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${className}`}>
      {children}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────
export default function PremiumTemplate({
  campsite, address, allImages, heroContributors, promoImageSrc, defaultImage, weatherForecast,
  fireDanger, nearbyCampsites, similarCampsites, nearbyActivities,
  popularRoutes, shopProducts, regions, isAccom, isCampingType,
}: PremiumTemplateProps) {

  const location = address ? `${address.town || ''}, ${address.state || ''}`.replace(/^, |, $/g, '') : '';
  const hasBooking = !!campsite.booking_url;
  const hasPhone = !!address?.phone;

  const chipFeatures = (campsite.features || [])
    .filter((f: Feature) => CHIP_FEATURES.has((f.desc || '').toLowerCase()))
    .slice(0, 8);

  const hasFishing = campsite.features?.some((f: Feature) =>
    ['Fishing Spot', 'Beach Fishing', 'Open Water Fishing', 'Rock Fishing'].includes(f.desc || '')
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">

      {/* ── HERO — Reuse existing ImageGalleryHero component ──── */}
      <ImageGalleryHero
        images={allImages}
        contributors={heroContributors}
        promoImageSrc={promoImageSrc}
        campSiteName={campsite.name}
        defaultImage={defaultImage}
        campsiteInfo={{
          name: campsite.name,
          location: entityLocationLabel(address ? [address] : null, campsite.regions),
          rating: campsite.rating && !isNaN(Number(campsite.rating)) && Number(campsite.rating) > 0 ? Number(campsite.rating) : undefined,
          starClassification: campsite.star_classification || undefined,
        }}
      />

      {/* ── REVIEW TEASER below hero ──────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5 pb-0">
        <ReviewTeaser entityId={campsite.id} />
      </div>

      {/* ── QUICK FACTS / ATTRIBUTE CHIPS ─────────────────────── */}
      {chipFeatures.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
          <div className="flex flex-wrap gap-2.5 rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            {/* Entity type badge — primary brand blue */}
            <span className="px-4 py-2 rounded-full bg-sky-700 text-white text-sm font-semibold tracking-wide">
              {getTypeLabel(campsite.entity_type)}
            </span>
            {chipFeatures.map((f: Feature, i: number) => (
              <span
                key={i}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                  (f.desc || '').toLowerCase().includes('no ')
                    ? 'bg-red-50 border-red-200 text-red-700'
                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100'
                }`}
              >
                {f.desc}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* ── STICKY NAV ────────────────────────────────────────── */}
      <StickyNav
        sections={[
          { id: 'about-section', label: 'About', show: true },
          { id: 'features-section', label: 'Features', show: !!(campsite.features && campsite.features.length > 0) },
          { id: 'weather-section', label: 'Weather', show: !!weatherForecast },
          { id: 'reviews-section', label: 'Reviews', show: true },
          { id: 'nearby-section', label: 'Nearby', show: nearbyCampsites.length > 0 },
          { id: 'similar-section', label: 'Similar', show: similarCampsites.length > 0 },
        ]}
      />

      {/* ── MAIN CONTENT GRID ─────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

          {/* ── LEFT COLUMN — Main Content ──────────────────── */}
          <div className="lg:col-span-2 space-y-7">

            {/* Premium Badge */}
            {campsite.is_premium && (
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200/80 rounded-xl px-5 py-2.5">
                <span className="text-amber-500 text-lg">&#9733;</span>
                <span className="text-amber-800 font-semibold text-sm tracking-wide">Premium Listing</span>
              </div>
            )}

            {/* ─ About Section ─────────────────────────────── */}
            <SectionCard id="about-section">
              <SectionHeading className="mb-5">{getAboutLabel(campsite.entity_type)}</SectionHeading>

              {/* Short description / tagline */}
              {campsite.short_desc && campsite.desc && !isRedundantTeaser(campsite.short_desc, campsite.desc) && (
                <p className="text-base text-slate-800 font-medium mb-5 pb-5 border-b border-slate-100 leading-7">
                  {looksLikeHtml(campsite.short_desc) ? stripHtml(campsite.short_desc) : campsite.short_desc}
                </p>
              )}

              {/* US (RIDB) descriptions arrive as HTML — sanitize and render as
                  markup; AU plain text takes the exact pre-existing path. */}
              <div className="text-[15px] leading-7 text-slate-600">
                {looksLikeHtml(campsite.desc || campsite.short_desc || '') ? (
                  <ShowMoreText
                    text={sanitizeDescriptionHtml(campsite.desc || campsite.short_desc || '')}
                    asHtml
                    maxLines={5}
                  />
                ) : (
                  <ShowMoreText
                    text={campsite.desc || campsite.short_desc || 'No description available.'}
                    maxLines={5}
                  />
                )}
              </div>
            </SectionCard>

            {/* ─ Directions ────────────────────────────────── */}
            {address?.directions && (
              <SectionCard>
                <SectionHeading className="mb-5 flex items-center gap-3">
                  <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-red-50">
                    <FaMapMarkerAlt className="text-red-500 text-base" />
                  </span>
                  How to Get There
                </SectionHeading>
                <div className="text-[15px] leading-7 text-slate-600">
                  <ShowMoreText text={address.directions} maxLines={4} />
                </div>
              </SectionCard>
            )}

            {/* ─ Features & Facilities ─────────────────────── */}
            {campsite.features && campsite.features.length > 0 && (
              <div id="features-section">
                <FeatureGrid features={campsite.features} />
              </div>
            )}

            {/* ─ Weather Forecast ──────────────────────────── */}
            {weatherForecast && (
              <div id="weather-section">
                <WeatherForecastComponent
                  forecast={weatherForecast}
                  campsiteName={campsite.name}
                />
              </div>
            )}

            {/* ─ Fishing Guide ─────────────────────────────── */}
            {hasFishing && campsite.id && (
              <div id="fishing-section">
                <FishingSpecies entityId={campsite.id} />
              </div>
            )}

            {/* ─ Nearby Activities & Tours ─────────────────── */}
            {nearbyActivities.length > 0 && (
              <SectionCard id="activities-section">
                <NearbyActivities
                  activities={nearbyActivities}
                  campsiteName={campsite.name}
                />
              </SectionCard>
            )}

            {/* ─ Community Reviews ─────────────────────────── */}
            <div id="reviews-section">
              <EntityReviews
                entityId={campsite.id}
                entityName={campsite.name}
              />
            </div>

            {/* ─ Popular Routes ─────────────────────────────── */}
            {popularRoutes.length > 0 && (
              <PopularRoutesComponent
                routes={popularRoutes}
                currentLocation={address?.town}
                heroImage={allImages?.[0]}
              />
            )}

            {/* ─ Campervan hire cross-sell ─────────────────── */}
            <VanHireStrip />

            {/* ─ Nearby Campsites ──────────────────────────── */}
            {nearbyCampsites.length > 0 && (
              <SectionCard id="nearby-section">
                <div className="flex justify-between items-center mb-6">
                  <SectionHeading>
                    Nearby {isAccom ? 'Accommodation' : 'Campsites'}
                  </SectionHeading>
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                    within 50km
                  </span>
                </div>
                <NearbyCampsitesComponent
                  campsites={nearbyCampsites}
                  currentCampsiteName={campsite.name}
                  defaultImage={defaultImage}
                />
              </SectionCard>
            )}

            {/* ─ Similar Campsites ─────────────────────────── */}
            {similarCampsites.length > 0 && (
              <SectionCard id="similar-section">
                <div className="flex justify-between items-center mb-6">
                  <SectionHeading>
                    Similar {isAccom ? 'Accommodation' : 'Campsites'}
                  </SectionHeading>
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full">
                    based on features
                  </span>
                </div>
                <div className="space-y-4">
                  {similarCampsites.map((similar) => (
                    <Link
                      key={similar.id}
                      href={withBase(`/camp-sites/${encodeURIComponent(
                        (similar.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                      )}`)}
                      className="block group"
                    >
                      <div className="border border-slate-200/80 rounded-xl overflow-hidden hover:border-sky-300 hover:shadow-md transition-all duration-200 bg-white">
                        <div className="flex">
                          <div className="relative w-32 h-32 flex-shrink-0 bg-slate-100">
                            <Image
                              src={similar.image_src || defaultImage}
                              alt={similar.name}
                              fill
                              className="object-cover group-hover:scale-105 transition-transform duration-200"
                              sizes="128px"
                            />
                          </div>
                          <div className="flex-1 p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg text-slate-900 group-hover:text-sky-700 transition-colors line-clamp-1">
                                  {similar.name}
                                </h4>
                                <p className="text-sm text-slate-500 mt-0.5">
                                  {entityLocationLabel(similar.addresses, (similar as { regions?: { name?: string }[] }).regions)}
                                  {similar.distance_km !== undefined && (
                                    <span className="text-slate-400 ml-2">
                                      &middot; {Math.round(typeof similar.distance_km === 'string' ? parseFloat(similar.distance_km) : similar.distance_km)} km away
                                    </span>
                                  )}
                                </p>
                              </div>
                              {similar.similarity_label && (
                                <span className={`
                                  px-3 py-1 text-xs font-medium rounded-full border whitespace-nowrap ml-3
                                  ${similar.similarity_label === 'Very Similar' ? 'bg-sky-50 text-sky-700 border-sky-200' :
                                    similar.similarity_label === 'Similar' ? 'bg-slate-50 text-slate-600 border-slate-200' :
                                    'bg-slate-50 text-slate-500 border-slate-200'}
                                `}>
                                  {similar.similarity_label}
                                </span>
                              )}
                            </div>
                            {similar.common_features && similar.common_features.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs text-slate-400 mb-2">Matching features:</p>
                                <div className="flex flex-wrap gap-2">
                                  {similar.common_features.slice(0, 6).map((feature, idx) => (
                                    <div
                                      key={idx}
                                      className="w-8 h-8 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center"
                                      title={feature}
                                    >
                                      <FeatureIcon feature={{ id: idx, desc: feature, feature_type: 'ICON', entities: [] }} />
                                    </div>
                                  ))}
                                  {similar.common_features.length > 6 && (
                                    <div className="w-8 h-8 bg-slate-50 border border-slate-100 rounded-lg flex items-center justify-center text-xs text-slate-500 font-medium">
                                      +{similar.common_features.length - 6}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            {similar.rating && !isNaN(Number(similar.rating)) && Number(similar.rating) > 0 && (
                              <div className="flex items-center mt-2 text-sm text-slate-600">
                                <FaStar className="text-amber-400 mr-1" />
                                <span>{similar.rating}/5</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                  <div className="text-center pt-3">
                    <Link
                      href={withBase(`/results?search_type=similar&entity_id=${campsite.id}`)}
                      className="text-sky-700 hover:text-sky-800 text-sm font-semibold inline-flex items-center gap-1"
                    >
                      View all similar campsites
                      <span>&rarr;</span>
                    </Link>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>

          {/* ── RIGHT COLUMN — Sidebar ───────────────────────── */}
          <div>
            <div className="sticky top-20 space-y-5">

              {/* Primary Actions Card */}
              <SidebarCard className="p-6 lg:p-7">
                <CampsiteActions
                  booking_url={campsite.booking_url}
                  latitude={address?.gps_latitude}
                  longitude={address?.gps_longitude}
                  phone={address?.phone}
                  entityId={campsite.id}
                  entityName={campsite.name}
                />
              </SidebarCard>

              {/* Fire Danger Widget — camping types only (AU ratings) */}
              {BRAND.showFireDanger && weatherForecast !== null && isCampingType && (() => {
                const wf = weatherForecast as WeatherForecast;
                return (
                  <FireDangerWidget
                    days={wf.days.slice(0, 3).map(d => ({
                      date: d.date,
                      tempMax: d.tempMax,
                      humidityMin: d.humidityMin ?? 50,
                      windMax: d.windspeed,
                      precipSum: d.precipitation,
                    }))}
                    state={address?.state}
                  />
                );
              })()}

              {/* Nearby Fuel Stations */}
              {BRAND.showFuelStations && (
                <NearbyFuelStations
                  latitude={address?.gps_latitude}
                  longitude={address?.gps_longitude}
                  state={address?.state}
                  radiusKm={30}
                  campsiteName={campsite.name}
                />
              )}

              {/* Mini Campsite Explorer */}
              <SidebarExplorer
                locationName={address?.town}
                state={address?.state}
                entityType={campsite.entity_type}
                regions={regions}
              />

              {/* Shop Gear CTA (Shopify — camping-australia only) */}
              {BRAND.showShopifyGear && (
                <ShopGearCTA
                  features={campsite.features}
                  products={shopProducts}
                  entityId={campsite.id}
                  entityName={campsite.name}
                  entityType={campsite.entity_type}
                />
              )}

              {/* Community & Ownership */}
              <SidebarCard className="p-6">
                <h3 className="text-base font-semibold text-slate-900 mb-4">Share Your Experience</h3>
                <SidebarReviewCTAs entityName={campsite.name} />
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">
                    Own This {isAccom ? 'Property' : 'Campsite'}?
                  </h4>
                  <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                    Claim your listing to manage information, respond to reviews, and update details.
                  </p>
                  <ClaimSiteButton entity={campsite} className="w-full" />
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <Link
                    href={withBase("/recommend-campsite")}
                    className="text-sm text-sky-700 hover:text-sky-800 font-medium hover:underline"
                  >
                    Missing a campsite? Recommend one &rarr;
                  </Link>
                </div>
              </SidebarCard>

              {/* Location Card */}
              {address && (
                <SidebarCard className="overflow-hidden">
                  <AddressCard
                    address={address}
                    campsiteName={campsite.name}
                    phoneNumber={address.phone || ''}
                    website={campsite.url || ''}
                    hosts={campsite.hosts ? [campsite.hosts] : []}
                    openingHours=""
                    booking_url={campsite.booking_url}
                    entityId={campsite.id}
                    entityName={campsite.name}
                    campsiteType={campsite.item}
                    managedBy={campsite.managed_by}
                    managedByType={campsite.managed_by_type}
                    email={address.email}
                  />
                </SidebarCard>
              )}

              {/* Brand / Operator Logo */}
              {campsite.brand_image_src && (
                <SidebarCard className="p-6 lg:p-7 flex flex-col items-center">
                  <Image
                    src={campsite.brand_image_src}
                    alt={`${campsite.hosts || campsite.name} logo`}
                    width={180}
                    height={80}
                    className="object-contain"
                    style={{ maxHeight: 80 }}
                  />
                  {campsite.hosts && (
                    <span className="text-sm text-slate-500 mt-2.5 font-medium">{campsite.hosts}</span>
                  )}
                </SidebarCard>
              )}

              {/* Regions */}
              {regions.length > 0 && (
                <SidebarCard className="p-6 lg:p-7">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Explore Regions</h3>
                  <div className="space-y-1">
                    {regions.map((region: any) => (
                      <Link
                        key={region.id}
                        href={withBase(`/regions/${region.name.toLowerCase().replace(/&/g, 'and').replace(/ +/g, '-').replace(/--+/g, '-')}`)}
                        className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors group"
                      >
                        <div>
                          <span className="font-medium text-sky-700 group-hover:text-sky-800 text-sm">
                            {region.name}
                          </span>
                          {region.state && (
                            <span className="text-xs text-slate-400 ml-2">{region.state}</span>
                          )}
                        </div>
                        <FaChevronRight className="text-xs text-slate-300 group-hover:text-slate-400" />
                      </Link>
                    ))}
                  </div>
                </SidebarCard>
              )}

              {/* Additional Information */}
              {(campsite.star_classification || campsite.rating || campsite.verified_by || campsite.hosts) && (
                <SidebarCard className="p-6">
                  <h3 className="text-base font-semibold text-slate-900 mb-4">Additional Information</h3>
                  <div className="space-y-3 text-sm">
                    {campsite.hosts && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Managed by</span>
                        <span className="font-medium text-slate-800">{campsite.hosts}</span>
                      </div>
                    )}
                    {campsite.star_classification && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">AAA Tourism</span>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }, (_, i) => (
                            <FaStar
                              key={i}
                              className={i < Math.floor(campsite.star_classification!) ? 'text-amber-400' : 'text-slate-200'}
                              size={14}
                            />
                          ))}
                          {campsite.star_classification % 1 >= 0.5 && (
                            <span className="text-xs text-slate-400 ml-1">&frac12;</span>
                          )}
                        </div>
                      </div>
                    )}
                    {campsite.rating && !campsite.star_classification && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Rating</span>
                        <div className="flex items-center gap-1">
                          <FaStar className="text-amber-400" size={14} />
                          <span className="font-medium text-slate-800">{campsite.rating}/5</span>
                        </div>
                      </div>
                    )}
                    {campsite.verified_by && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Verified by</span>
                        <span className="font-medium text-slate-800">{campsite.verified_by}</span>
                      </div>
                    )}
                  </div>
                </SidebarCard>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ── MOBILE STICKY CTA BAR ────────────────────────────── */}
      <MobileCTABar campsite={campsite} />
    </div>
  );
}
